import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ExternalLink, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Full-screen blocking gate that forces the logged-in user to accept the
 * current legal documents whenever ``/api/me/legal-status`` reports any
 * missing or outdated acceptance.
 *
 * Three states the user can be in:
 * - ``current``  — nothing to do (gate stays hidden)
 * - ``missing``  — existing pre-rollout account without any acceptance yet
 * - ``outdated`` — document version bumped since the last acceptance
 *
 * Until acceptance is recorded the gate covers the entire viewport and
 * the only allowed action besides accepting is the global logout
 * (also rendered inside the gate).
 */
export default function LegalAcceptanceGate({ children }) {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null); // { has_missing_acceptances, documents }
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      setStatus(null);
      setChecked(false);
      return;
    }
    setChecking(true);
    api.get("/me/legal-status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => { setChecking(false); setChecked(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const blocked = !!user && checked && status?.has_missing_acceptances;

  const handleAccept = async () => {
    if (!accepted) {
      toast.error("Bitte das Kontrollkästchen aktivieren, um zuzustimmen.");
      return;
    }
    setSubmitting(true);
    try {
      const slugs = (status?.documents || [])
        .filter((d) => d.status !== "current")
        .map((d) => d.slug);
      await api.post("/me/accept-legal", { slugs });
      // Re-check the status — should now be clean
      const fresh = await api.get("/me/legal-status");
      setStatus(fresh.data);
      if (!fresh.data.has_missing_acceptances) {
        toast.success("Vielen Dank — Zustimmung dokumentiert.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!blocked) return children;

  const docs = (status?.documents || []).filter((d) => d.status !== "current");
  const headline = docs.some((d) => d.status === "outdated")
    ? "Aktualisierte Rechtsdokumente"
    : "Zustimmung erforderlich";

  return (
    <>
      {/* Backdrop covers the entire app — children are NOT rendered while gated */}
      <div
        data-testid="legal-acceptance-gate"
        className="fixed inset-0 z-[60] bg-[#09090B]/95 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      >
        <div className="w-full max-w-lg bg-[#18181B] border border-zinc-800 rounded-2xl shadow-2xl">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#00C964]/15 flex items-center justify-center shrink-0">
                <ShieldCheck size={22} className="text-[#00E575]" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-semibold text-zinc-50">{headline}</h2>
                <p className="text-sm text-zinc-400 mt-1">
                  Um die App weiter zu nutzen, bestätige bitte die aktuellen
                  Rechtsdokumente. Deine Zustimmung wird mit Datum und
                  Versionsnummer revisionssicher protokolliert.
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-3">
            {docs.map((d) => (
              <div
                key={d.slug}
                data-testid={`legal-gate-doc-${d.slug}`}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#09090B] border border-zinc-800"
              >
                <div className="min-w-0">
                  <div className="font-medium text-zinc-100 truncate">{d.title}</div>
                  <div className="text-xs text-zinc-300 mt-0.5">
                    Stand {d.effective_date} · v{d.current_version}
                    {d.status === "outdated" && (
                      <span className="ml-2 inline-flex items-center gap-1 text-amber-400">
                        <AlertTriangle size={11} /> Neue Version
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to={`/legal/${d.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-200 transition shrink-0"
                  data-testid={`legal-gate-open-${d.slug}`}
                >
                  Öffnen <ExternalLink size={11} />
                </Link>
              </div>
            ))}

            <label className="flex items-start gap-3 cursor-pointer select-none pt-2">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="legal-gate-accept-checkbox"
                className="mt-1 h-5 w-5 rounded border-zinc-600 bg-zinc-900 accent-[#00C964] cursor-pointer shrink-0"
              />
              <span className="text-sm text-zinc-300 leading-relaxed">
                Ich habe die oben genannten Dokumente gelesen und akzeptiere
                sie in der jeweils aktuellen Fassung.
              </span>
            </label>
          </div>

          <div className="p-6 pt-2 border-t border-zinc-800 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
            <button
              type="button"
              onClick={logout}
              data-testid="legal-gate-logout-btn"
              className="text-xs text-zinc-300 hover:text-zinc-300 transition"
            >
              Abmelden, ohne zuzustimmen
            </button>
            <Button
              type="button"
              onClick={handleAccept}
              disabled={!accepted || submitting}
              data-testid="legal-gate-confirm-btn"
              className="bg-[#00E575] text-[#09090B] font-bold hover:bg-[#00C964] disabled:opacity-50"
            >
              {submitting ? <Loader2 className="animate-spin" size={14} /> : "Zustimmen & fortfahren"}
            </Button>
          </div>
        </div>
      </div>
      {/* While gated we still render children behind the backdrop so the auth
          context, providers and toaster keep working. The backdrop covers
          them visually + intercepts every click. */}
      <div aria-hidden="true" className="opacity-0 pointer-events-none">
        {children}
      </div>
    </>
  );
}

// Convenience: a small "compliance status" badge usable in profile page later.
export const LegalStatusBadge = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/me/legal-status").then((r) => setS(r.data)).catch(() => setS(null));
  }, []);
  if (!s) return null;
  if (s.has_missing_acceptances) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-400">
        <AlertTriangle size={11} /> Zustimmung ausstehend
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
      <ShieldCheck size={11} /> Rechtsdokumente akzeptiert
    </span>
  );
};
